import { forwardRef } from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import classNames from "classnames/bind";
import { BugIcon } from "../icons";
import { useAutoReveal } from "../hooks/useAutoReveal";
import { useReleaseVersion } from "../hooks/useReleaseVersion";
import styles from "./ReleaseTrigger.module.scss";

const cx = classNames.bind(styles);

type ReleaseTriggerProps = {
	onClick?: () => void;
	className?: string;
};

/**
 * Trigger showing a "debug" icon that briefly reveals the current release name on mount.
 */
export const ReleaseTrigger = forwardRef<HTMLButtonElement, ReleaseTriggerProps>(
	({ onClick, className, ...triggerProps }, ref) => {
		const version = useReleaseVersion();
		const { state, collapse } = useAutoReveal();
		const prefersReducedMotion = useReducedMotion();

		const isRevealed = state === "revealed" && Boolean(version);

		return (
			<motion.button
				{...triggerProps}
				ref={ref}
				type="button"
				layout
				whileTap={{ scale: 0.97 }}
				contentEditable={false}
				data-revealed={isRevealed}
				transition={
					prefersReducedMotion
						? { duration: 0 }
						: {
								layout: { type: "spring", visualDuration: 0.35, bounce: 0.3 },
								scale: { type: "spring", stiffness: 500, damping: 22 },
							}
				}
				className={cx("release-trigger", className)}
				onClick={onClick}
				onPointerEnter={collapse}
				onFocus={collapse}
				aria-label="Ouvrir les outils de développement"
			>
				<motion.span
					layout="position"
					transition={
						prefersReducedMotion
							? { duration: 0 }
							: { layout: { type: "spring", visualDuration: 0.35, bounce: 0 } }
					}
					className={cx("release-trigger__icon")}
					aria-hidden="true"
				>
					<BugIcon />
				</motion.span>
				<AnimatePresence initial={false} mode="popLayout">
					{isRevealed && (
						<motion.span
							layout
							className={cx("release-trigger__label")}
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							transition={{ duration: prefersReducedMotion ? 0 : 0.09 }}
						>
							<span className={cx("release-trigger__caption")}>Release</span>
							<span className={cx("release-trigger__version")}>{version}</span>
						</motion.span>
					)}
				</AnimatePresence>
			</motion.button>
		);
	}
);

ReleaseTrigger.displayName = "ReleaseTrigger";
